// Storefront side of the payment platform. The backend owns providers (Medusa Payment Module);
// the storefront only needs to know how to present each `provider_id`. Checkout code reads the
// adapter's flow and capabilities and never branches on a provider or brand name.

// How the customer finishes paying after choosing the method.
//   none     - nothing else to collect (system/manual, mock)
//   redirect - leaves to a hosted page and comes back
//   embedded - provider widget/tokenization rendered inside checkout
export const PAYMENT_FLOWS = Object.freeze(['none', 'redirect', 'embedded']);

// Minimal capability vocabulary for the gateway shortlist (approved by the owner on 2026-10-15).
export const PAYMENT_CAPABILITIES = Object.freeze([
  'async_confirmation', // can stay pending after checkout (PIX, boleto, payment link)
  'manual_capture',
  'cancel',
  'refund',
  'partial_refund',
  'installments', // card payment split into installments chosen at checkout
]);

const adapter = ({ id, label, description, flow, capabilities, testOnly = false }) => {
  if (!PAYMENT_FLOWS.includes(flow)) {
    throw new Error(`Unknown payment flow "${flow}" for ${id}`);
  }
  const unknown = capabilities.filter((c) => !PAYMENT_CAPABILITIES.includes(c));
  if (unknown.length) {
    throw new Error(`Unknown payment capabilities ${unknown.join(', ')} for ${id}`);
  }
  return Object.freeze({ id, label, description, flow, capabilities: Object.freeze([...capabilities]), testOnly });
};

export const paymentAdapters = Object.freeze({
  pp_system_default: adapter({
    id: 'pp_system_default',
    label: 'Pagamento combinado com a loja',
    description: 'A Loop Coffee confirma o pagamento com você depois do pedido.',
    flow: 'none',
    capabilities: ['manual_capture', 'cancel', 'refund', 'partial_refund'],
  }),
  pp_mock_mock: adapter({
    id: 'pp_mock_mock',
    label: 'Pagamento de teste',
    description: 'Simulado: nenhuma cobrança é feita.',
    flow: 'none',
    capabilities: ['async_confirmation', 'manual_capture', 'cancel', 'refund', 'partial_refund'],
    testOnly: true,
  }),
  pp_asaas_asaas: adapter({
    id: 'pp_asaas_asaas',
    label: 'Pix ou cartão (Asaas)',
    description: 'Você será levado ao ambiente seguro da Asaas para pagar com Pix ou cartão, em até 3x.',
    flow: 'redirect',
    capabilities: ['async_confirmation', 'cancel', 'refund', 'partial_refund', 'installments'],
  }),
});

/** Adapter for a provider id, or null: an unknown provider is never offered nor rendered. */
export function resolvePaymentAdapter(providerId) {
  return Object.prototype.hasOwnProperty.call(paymentAdapters, providerId)
    ? paymentAdapters[providerId]
    : null;
}

export const hasCapability = (adapterOrNull, capability) =>
  !!adapterOrNull && adapterOrNull.capabilities.includes(capability);

/**
 * Payment options for the region, in the order the API returned them. `providers` is the
 * `payment_providers` array of `GET /store/payment-providers?region_id=...`. Providers the
 * storefront cannot present are left out and reported in `unsupported`.
 */
export function listCheckoutPaymentOptions(providers) {
  const options = [];
  const unsupported = [];
  for (const provider of providers || []) {
    if (provider && provider.is_enabled === false) {
      continue;
    }
    const id = provider && provider.id;
    const found = resolvePaymentAdapter(id);
    if (found) {
      options.push(found);
    } else {
      unsupported.push(id);
    }
  }
  return { options, unsupported };
}

const PAYMENT_STATES = Object.freeze({
  authorized: { state: 'confirmed', isPaid: true, label: 'Pagamento autorizado' },
  partially_captured: { state: 'confirmed', isPaid: true, label: 'Pagamento confirmado' },
  captured: { state: 'confirmed', isPaid: true, label: 'Pagamento confirmado' },
  partially_refunded: { state: 'confirmed', isPaid: true, label: 'Pagamento confirmado (reembolso parcial)' },
  refunded: { state: 'refunded', isPaid: false, label: 'Pagamento reembolsado' },
  canceled: { state: 'canceled', isPaid: false, label: 'Pagamento cancelado' },
  not_paid: { state: 'pending', isPaid: false, label: 'Aguardando pagamento' },
  awaiting: { state: 'pending', isPaid: false, label: 'Aguardando confirmação do pagamento' },
  requires_action: { state: 'pending', isPaid: false, label: 'Pagamento precisa de uma ação sua' },
  partially_authorized: { state: 'pending', isPaid: false, label: 'Aguardando confirmação do pagamento' },
});

/**
 * Customer-facing reading of an order's `payment_status`. Anything not known to be confirmed is
 * shown as pending: the storefront never declares an order paid on its own.
 */
export function describePaymentStatus(paymentStatus) {
  return (
    PAYMENT_STATES[paymentStatus] || {
      state: 'pending',
      isPaid: false,
      label: 'Aguardando confirmação do pagamento',
    }
  );
}
